import type { LoadingValue } from "@glide/computation-model-types";
import { isLoadingValue } from "@glide/computation-model-types";
import {
    type ActionDescription,
    type LegacyPropertyDescription,
    type PropertyDescription,
    ActionKind,
    makeColumnProperty,
} from "@glide/app-description";
import type { TableColumn } from "@glide/type-schema";
import {
    type AppDescriptionContext,
    type ActionAvailability,
    makeInlineTemplatePropertyDescriptor,
    type PropertyDescriptor,
} from "@glide/function-utils";
import type {
    WireActionResult,
    WireActionResultBuilder,
    WireActionHydrator,
    WireActionInflationBackend,
    WireValueGetter,
    WireValueInflationBackend,
    WireRowHydrationValueProvider,
} from "@glide/wire";
import type { StaticActionContext } from "../static-context";
import { type ActionDescriptor, ActionGroup } from "./action-descriptor";
import { type DescriptionToken, actionAvailabilityApps } from "./action-handler";
import { BaseActionHandler, tokenForProperty } from "./base";
import { ICON_BASE, PURPLE_500 } from "../plugins/icon-colors";
import type { GlideIconProps } from "@glide/plugins";
import { makeMailtoURL, nullToUndefined, type EmailClient } from "@glide/support";
import { getAppFeatures, getEmailClient } from "@glide/common-core/dist/js/components/SerializedApp";
import { asMaybeString } from "@glide/common-core/dist/js/computation-model/data";

interface AdditionalEmailProperties {
    readonly subjectProperty?: LegacyPropertyDescription;
    readonly bodyProperty?: LegacyPropertyDescription;
    readonly ccProperty?: LegacyPropertyDescription;
    readonly bccProperty?: LegacyPropertyDescription;
}

function makeEmailPropertyDescriptors(
    emailPropertyName: string,
    withAdditionalProperties: boolean
): readonly PropertyDescriptor[] {
    const props: PropertyDescriptor[] = [
        makeInlineTemplatePropertyDescriptor(emailPropertyName, "To", "Recipient", true, "withLabel", {
            preferredType: "email-address",
            isDefaultCaption: true,
            columnFirst: true,
            preferredNames: ["email"],
        }),
    ];

    if (withAdditionalProperties) {
        props.push(
            makeInlineTemplatePropertyDescriptor("subjectProperty", "Subject", "Email subject", false, "withLabel", {
                emptyByDefault: true,
                searchable: false,
                columnFirst: true,
            }),
            makeInlineTemplatePropertyDescriptor("bodyProperty", "Body", "Email text", false, "withLabel", {
                emptyByDefault: true,
                searchable: false,
                columnFirst: true,
            }),
            makeInlineTemplatePropertyDescriptor("ccProperty", "CC", "Other recipients", false, "withLabel", {
                preferredType: "email-address",
                emptyByDefault: true,
                searchable: false,
                columnFirst: true,
            }),
            makeInlineTemplatePropertyDescriptor("bccProperty", "BCC", "Silent recipients", false, "withLabel", {
                preferredType: "email-address",
                emptyByDefault: true,
                searchable: false,
                columnFirst: true,
            })
        );
    }

    return props;
}

interface EmailGetters {
    readonly recipientGetter: WireValueGetter;
    readonly subjectGetter: WireValueGetter;
    readonly bodyGetter: WireValueGetter;
    readonly ccGetter: WireValueGetter;
    readonly bccGetter: WireValueGetter;

    // This shouldn't really be in here, but it makes using this much more
    // convenient.
    readonly emailClient: EmailClient;
}

function makeEmailGetters(
    ib: WireValueInflationBackend,
    recipientDesc: LegacyPropertyDescription | undefined,
    desc: AdditionalEmailProperties | undefined
): EmailGetters | undefined {
    if (recipientDesc === undefined || desc === undefined) return undefined;

    const [recipientGetter, recipientType] = ib.getValueGetterForProperty(recipientDesc, false);
    const [subjectGetter, subjectType] = ib.getValueGetterForProperty(desc.subjectProperty, true);
    const [bodyGetter, bodyType] = ib.getValueGetterForProperty(desc.bodyProperty, true);
    const [ccGetter, ccType] = ib.getValueGetterForProperty(desc.ccProperty, false);
    const [bccGetter, bccType] = ib.getValueGetterForProperty(desc.bccProperty, false);

    if (
        recipientType === undefined &&
        subjectType === undefined &&
        bodyType === undefined &&
        ccType === undefined &&
        bccType === undefined
    ) {
        return undefined;
    }
    const emailClient = getEmailClient(getAppFeatures(ib.adc.appDescription));

    return { recipientGetter, subjectGetter, bodyGetter, ccGetter, bccGetter, emailClient };
}

function hydrateEmailURL(vp: WireRowHydrationValueProvider, getters: EmailGetters): string | undefined | LoadingValue {
    const { recipientGetter, subjectGetter, bodyGetter, ccGetter, bccGetter, emailClient } = getters;

    const recipientValue = nullToUndefined(recipientGetter(vp));
    if (isLoadingValue(recipientValue)) return recipientValue;
    const recipient = asMaybeString(recipientValue);

    const subjectValue = nullToUndefined(subjectGetter(vp));
    if (isLoadingValue(subjectValue)) return subjectValue;
    const subject = asMaybeString(subjectValue);

    const bodyValue = nullToUndefined(bodyGetter(vp));
    if (isLoadingValue(bodyValue)) return bodyValue;
    const body = asMaybeString(bodyValue);

    const ccValue = nullToUndefined(ccGetter(vp));
    if (isLoadingValue(ccValue)) return ccValue;
    const cc = asMaybeString(ccValue);

    const bccValue = nullToUndefined(bccGetter(vp));
    if (isLoadingValue(bccValue)) return bccValue;
    const bcc = asMaybeString(bccValue);

    return makeMailtoURL(emailClient, recipient, subject, body, cc, bcc);
}

interface SendEmailActionDescription extends ActionDescription, AdditionalEmailProperties {
    readonly kind: ActionKind.SendEmailWithArgs;
    // This property name is also hardcoded in ##convertLegacyActions
    readonly email?: PropertyDescription;
}

export class SendEmailWithArgsHandler extends BaseActionHandler<SendEmailActionDescription> {
    public readonly kind = ActionKind.SendEmailWithArgs;
    public readonly iconName: GlideIconProps = {
        icon: "st-compose-email",
        kind: "stroke",
        strokeFgColor: ICON_BASE,
        strokeColor: PURPLE_500,
    };

    public readonly name: string = "Compose email";

    public get availability(): ActionAvailability {
        return actionAvailabilityApps;
    }

    public getDescriptor(): ActionDescriptor {
        return {
            name: this.name,
            group: ActionGroup.Communication,
            groupItemOrder: 1,
            needsScreenContext: true,
            properties: makeEmailPropertyDescriptors("email", true),
        };
    }

    public newDefaultActionDescription(c: TableColumn): SendEmailActionDescription | undefined {
        if (c.type.kind !== "email-address") return undefined;
        return { kind: ActionKind.SendEmailWithArgs, email: makeColumnProperty(c.name) };
    }

    public getTokenizedDescription(
        desc: SendEmailActionDescription,
        env: StaticActionContext<AppDescriptionContext>
    ): readonly DescriptionToken[] | undefined {
        const columnToken = tokenForProperty(desc.email, env);
        if (columnToken === undefined) return undefined;

        return [
            {
                kind: "string",
                value: "to ",
            },
            columnToken,
        ];
    }

    public inflate(
        ib: WireActionInflationBackend,
        desc: SendEmailActionDescription,
        arb: WireActionResultBuilder
    ): WireActionHydrator | WireActionResult {
        const getters = makeEmailGetters(ib, desc.email, desc);
        if (getters === undefined) return arb.inflationError("Invalid email");

        return (vp, skipLoading) => {
            const url = hydrateEmailURL(vp, getters);
            if (isLoadingValue(url)) return arb.maybeSkipLoading(skipLoading, "Email address");
            if (url === undefined) return arb.error(true, "Invalid email");

            return async ab => {
                ab.actionCallbacks.openLink(url);
                return arb.addData({ url }).success();
            };
        };
    }
}
